import { cn } from "cn";
import { format, isValid } from "date-fns";
import { cs } from "date-fns/locale";
import { CalendarIcon, XIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const toDate = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) return undefined;
  const d = value instanceof Date ? value : new Date(value);
  return isValid(d) ? d : undefined;
};

type DatePickerProps = {
  id?: string;
  value: Date | string | null | undefined;
  onChange: (date: Date | null) => void;
  /** Fired when popover closes. */
  onBlur?: () => void;
  placeholder?: string;
  /** date-fns format string. */
  dateFormat?: string;
  disabled?: boolean;
  clearable?: boolean;
  invalid?: boolean;
  className?: string;
};

/** shadcn date picker: outline button trigger + Calendar in Popover. Czech locale. */
function DatePicker({
  id,
  value,
  onChange,
  onBlur,
  placeholder = "Vyberte datum",
  dateFormat = "dd. MM. yyyy",
  disabled = false,
  clearable = false,
  invalid = false,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = toDate(value);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) onBlur?.();
  };

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger
          render={
            <Button
              id={id}
              type="button"
              variant="outline"
              disabled={disabled}
              aria-invalid={invalid || undefined}
              data-empty={!selected}
              className={cn(
                "h-8 w-full justify-start px-2.5 font-normal data-[empty=true]:text-muted-foreground",
                clearable && selected && "pr-8"
              )}
            />
          }
        >
          <CalendarIcon className="text-muted-foreground" />
          <span className="truncate tabular-nums">
            {selected ? format(selected, dateFormat, { locale: cs }) : placeholder}
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            locale={cs}
            captionLayout="dropdown"
            selected={selected}
            defaultMonth={selected}
            onSelect={(d) => {
              onChange(d ?? null);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
      {clearable && selected && !disabled ? (
        <button
          type="button"
          aria-label="Vymazat datum"
          onClick={() => onChange(null)}
          className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
        >
          <XIcon className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export { DatePicker };
export type { DatePickerProps };
